import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
}

interface Contact {
  id: number
  display_name: string
}

interface MarketingEvent {
  id: number
  title: string | null
  activity_date: string | null
}

interface Props {
  customer: Customer
  contact: Contact | null
  events: MarketingEvent[]
}

export default function Index({ customer, contact, events }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Customers", href: "/customers" },
    { title: customer.display_name, href: `/customers/${customer.id}` },
    ...(contact
      ? [
          { title: "Contacts", href: `/customers/${customer.id}/contacts` },
          {
            title: `Marketing Events`,
            href: `/customers/${customer.id}/marketing_events?contact_id=${contact.id}`,
          },
        ]
      : [
          {
            title: "Marketing Events",
            href: `/customers/${customer.id}/marketing_events`,
          },
        ]),
  ]

  const heading = contact
    ? `Marketing Events for ${contact.display_name}`
    : `Marketing Events for ${customer.display_name}`

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Marketing Events - ${customer.display_name}`} />

      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">{heading}</h1>
        </div>

        {events.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/marketing_activities/${event.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {event.activity_date}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/marketing_activities/${event.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {event.title}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No marketing events found.</p>
        )}

        <div className="mt-4">
          <Link
            href={
              contact
                ? `/customers/${customer.id}/contacts`
                : `/customers/${customer.id}`
            }
            className="text-hpi-active-blue hover:underline"
          >
            {contact ? "Back to Contacts" : "Back to Customer"}
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
